import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import TableCard from "../../components/TableCard";
import SnackBar from "../../components/SnackBar";
import Loading from "../../components/Loading";
import WhiteButton from "../../components/WhiteButton";
import BlueButton from "../../components/BlueButton";
import BadgeStatus from "../Apuracoes/components/BadgeStatus";
import SelectFiltro, { FILTER_FIELD_SX } from "../Apuracoes/components/SelectFiltro";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { carregarComboItens } from "../Apuracoes/resolverGrupoEmpresa.util";
import {
  buscarTabelaNcm,
  atualizarItemTabelaNcm,
} from "../../services/api-planejamento-tributario.service";
import ModalNovoRegistro from "./ModalNovoRegistro";
import { TIPOS_ITEM, CLASSIFICACOES, UFS } from "./dominios";
import { CampoCnpj, formatarCnpj, useCnpjsCadastrados, useEmpresasDoGrupo } from "./CampoCnpj";
import { CamposAliquotaMetodo } from "./CamposAliquotaMetodo";
import {
  MOTIVO_ESCOPO_GLOBAL_IMUTAVEL,
  MOTIVO_SEM_PERMISSAO_GLOBAL,
  aliquotaParaTexto,
  ehLinhaGlobal,
  normalizarMetodo,
  payloadAliquotaMetodoAlterados,
  payloadNcmEscopoEdicao,
  rotuloMetodo,
} from "./aliquotaMetodo.util";
import {
  usePermissoes,
  CHAVES_TABELA_NCM_CREATE,
  CHAVES_TABELA_NCM_EDIT,
  CHAVES_TABELA_NCM_EDIT_GLOBAL,
} from "../../config/Context/PermissoesContext";
import styles from "./styles.module.css";

const COLUNAS = [
  "Cliente",
  "CNPJ",
  "Estado",
  "Item",
  "CEST",
  "NCM/SH",
  "Descrição",
  "Tipo",
  "Classificação",
  "Valor MVA",
  "Alíquota interna (%)",
  "Método de cálculo",
];

/** Sentinela do filtro de Cliente pra "Todas as Empresas (sem cliente)" — nunca colide com um
 * id_grupo_economico real (que é numérico). */
const SEM_CLIENTE = "__SEM_CLIENTE__";
/** Mesma ideia pro filtro de Estado — nunca colide com uma UF real (2 letras). */
const SEM_ESTADO = "__SEM_ESTADO__";
/** Item com Cliente e ainda sem CNPJ (legado pendente) — nunca colide com 14 dígitos. */
const SEM_CNPJ = "__SEM_CNPJ__";

/** Convenção SelectFiltro (mesma de ClassificacaoNcm/Usuarios): "" representa "Todos". */
const FILTROS_VAZIOS = {
  search: "",
  cliente: "",
  cnpj: "",
  estado: "",
  tipo: "",
  classificacao: "",
};

const EDICAO_VAZIA = {
  ncm: "",
  cest: "",
  descricao: "",
  tipo: null,
  classificacao: null,
  mva: "",
  aliquota: "",
  metodo: "",
  // Escopo do item no modal Editar — Cliente, CNPJ e Estado são editáveis em qualquer direção,
  // exceto na linha global (decisão 09-30: NCM, Item e escopo da linha global são imutáveis).
  clienteNovo: null,
  cnpjNovo: null,
  estadoNovo: null,
};

/**
 * Mesmas cores de src/pages/Apuracoes/ClassificacaoNcm/index.js (COR_POR_CLASSIFICACAO) —
 * a Tabela NCM é a fonte de dados daquela tela, então usar a paleta diferente aqui confundiria
 * o usuário que vê as duas.
 */
const COR_POR_CLASSIFICACAO = {
  Insumo: { bgcolor: "#e8f5e9", color: "#2e7d32" },
  Uso: { bgcolor: "#fff3e0", color: "#ad4e00" },
  Consumo: { bgcolor: "#e3f2fd", color: "#1565c0" },
};

function celulaClassificacao(valor) {
  const cores = COR_POR_CLASSIFICACAO[valor];
  if (!cores) return <BadgeStatus tom="neutro">Não classificado</BadgeStatus>;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "0.72rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...cores,
      }}
    >
      {valor}
    </Box>
  );
}

function celulaTipo(valor) {
  return <BadgeStatus tom="neutro">{valor || "—"}</BadgeStatus>;
}

/** .modal-grid do protótipo: grid-template-columns 1fr 1fr, gap 16px 18px. */
const GRID_MODAL = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: "16px 18px",
};
/** .span-2 => grid-column 1 / -1 (volta a "auto" quando o grid vira 1 coluna). */
const SPAN_2_MODAL = { gridColumn: { xs: "auto", sm: "1 / -1" } };
/** .modal-hint */
const HINT_MODAL = { fontSize: 12, color: "#888", marginTop: "6px", display: "block" };

/** Igual ao formatPercent do protótipo (tabela_ncm.html): 2 casas, vírgula pt-BR, "—" quando ausente. */
function formatarMva(valor) {
  if (valor == null || Number.isNaN(Number(valor))) return "—";
  return `${Number(valor).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * Mesma função de src/pages/Apuracoes/ClassificacaoNcm/index.js — as duas telas mostram os
 * mesmos NCMs e divergir na máscara confundiria. Só exibição: o valor gravado continua sendo
 * o que o usuário digitou (o card trata NCM/CEST como texto livre), e qualquer coisa fora do
 * formato esperado é devolvida crua em vez de ser "consertada".
 */
function formatarNcm(ncm) {
  const digitos = String(ncm || "").replace(/\D/g, "");
  if (digitos.length !== 8) return ncm || "—";
  return `${digitos.slice(0, 4)}.${digitos.slice(4, 6)}.${digitos.slice(6, 8)}`;
}

/** CEST tem 7 dígitos no formato XX.XXX.XX — mesma regra de fallback do NCM acima. */
function formatarCest(cest) {
  const digitos = String(cest || "").replace(/\D/g, "");
  if (digitos.length !== 7) return cest || "—";
  return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 7)}`;
}

/**
 * Trunca com reticências e mostra o texto inteiro no tooltip — usada em Cliente (card seção
 * 2.1, nomes de grupo econômico longos) e em Descrição, pro mesmo motivo: sem truncar, a
 * tabela força rolagem lateral pra caber as 9 colunas na tela.
 */
const celulaTruncada = (texto, maxWidth) => (
  <Tooltip title={texto || ""}>
    <span
      style={{
        display: "inline-block",
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      {texto || "—"}
    </span>
  </Tooltip>
);

/** Sem grupo econômico = item de public.ncms, válido para qualquer cliente (decisão de
 * produto 2026-09-21) — mostra um rótulo claro em vez de célula vazia, que pareceria erro. */
// Larguras enxugadas quando a coluna CNPJ entrou (11 colunas): com 170/240 a coluna Ações
// passava da borda. Enxugadas de novo (150/190 → 130/170) quando entraram Alíquota interna e
// Método de cálculo, junto com o padding menor e o título que quebra linha (styles.module.css).
// O texto inteiro continua no tooltip.
const celulaCliente = (nome) => celulaTruncada(nome || "Todas as Empresas", 130);
const celulaDescricao = (descricao) => celulaTruncada(descricao, 170);

/** Contrato TXM-594: 2 casas pt-BR, sem "%" (já está no título) e VAZIO quando null — vazio
 * aqui significa "herda", não "zero", por isso não usa o "—" do Valor MVA. */
const formatarAliquota = (valor) => aliquotaParaTexto(valor);

/** PMC / MVA / "Não se aplica" ('-') / "Herda" (null). "Herda" em cinza, pra não parecer um
 * método gravado na linha. */
function celulaMetodo(valor) {
  const rotulo = rotuloMetodo(valor);
  if (normalizarMetodo(valor) !== "") return rotulo;
  return (
    <Tooltip title="Herda do nível acima">
      <span style={{ color: "#9e9e9e" }}>{rotulo}</span>
    </Tooltip>
  );
}
/** Mesmo raciocínio do Cliente, aplicado ao Estado — sem UF é "válido em qualquer estado".
 * Texto puro, igual ao que a coluna Estado já era: não introduz um badge que não existia
 * pras linhas com UF normal, só substitui o texto quando nulo. */
function celulaEstado(estado) {
  return estado || "Todos os Estados";
}
/** Sem Cliente, CNPJ não se aplica ("—"). Com Cliente e sem CNPJ é legado pendente — badge de
 * alerta pra esses itens não passarem despercebidos (salvar qualquer edição exige o CNPJ). */
function celulaCnpj(item) {
  if (item.id_grupo_economico == null) return "—";
  if (item.cnpj) return formatarCnpj(item.cnpj);
  return <BadgeStatus tom="alerta">Sem CNPJ</BadgeStatus>;
}

const TabelaNcm = () => {
  // Mesmo padrão de ParceirosAssociados: uma flag por ação, lida do PermissoesContext. A API
  // valida de novo em cada rota — aqui é só pra não oferecer o que vai ser negado.
  const { pode } = usePermissoes();
  const podeCriar = pode(CHAVES_TABELA_NCM_CREATE);
  const podeEditar = pode(CHAVES_TABELA_NCM_EDIT);
  const podeEditarGlobal = pode(CHAVES_TABELA_NCM_EDIT_GLOBAL);

  const [data, setData] = useState([]);
  /** Registros crus da página atual (o `data` acima é a versão já formatada pra tabela). */
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [grupos, setGrupos] = useState([]);
  // abrirEdicao é chamado de dentro de montarLinhas (useCallback com deps [], memoizado desde
  // o 1º render — antes de grupos terminar de carregar). Ler `grupos` direto ali prenderia
  // abrirEdicao pra sempre no array vazio do mount, e o Cliente do modal Editar nunca
  // apareceria pré-preenchido. Ref não sofre esse problema: o objeto é estável entre renders,
  // só o .current muda, então quem lê gruposRef.current sempre pega o valor mais recente
  // independente de quando a closure que o envolve foi criada.
  const gruposRef = useRef([]);
  useEffect(() => {
    gruposRef.current = grupos;
  }, [grupos]);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  /** Valor exibido no campo de busca — separado de filtros.search pra não esperar o
   * debounce pra tela refletir o que o usuário está digitando. */
  const [buscaInput, setBuscaInput] = useState("");

  const [openNovo, setOpenNovo] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  const [formEdicao, setFormEdicao] = useState(EDICAO_VAZIA);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const abrirEdicao = (item) => {
    setItemEdicao(item);
    setFormEdicao({
      ncm: item.ncm || "",
      cest: item.cest || "",
      descricao: item.descricao || "",
      tipo: item.tipo || null,
      classificacao: item.classificacao || null,
      mva: item.mva != null ? String(item.mva) : "",
      aliquota: aliquotaParaTexto(item.aliq_interna),
      // Caixa alta pro select casar com a opção ('mva' legado → "MVA"). payloadAliquotaMetodoAlterados
      // compara com o valor CRU gravado, então o próximo save reenvia "MVA" e normaliza a linha
      // (a API recusa PUT numa linha 'mva' que não traga metodo_calculo explícito).
      metodo: normalizarMetodo(item.metodo_calculo),
      // Pré-preenche com o cliente atual do item (achado na lista já carregada por id) —
      // Cliente/Estado são editáveis em qualquer direção, então o Autocomplete abre já
      // mostrando o valor de hoje, exatamente como CEST/Tipo/Classificação abaixo.
      // gruposRef, não o state `grupos` direto — ver comentário no useRef acima.
      clienteNovo: item.id_grupo_economico != null
        ? gruposRef.current.find((g) => String(g.id) === String(item.id_grupo_economico)) || null
        : null,
      cnpjNovo: item.cnpj || null,
      estadoNovo: item.estado || null,
    });
  };

  const {
    empresas: empresasEdicao,
    carregando: carregandoEmpresasEdicao,
    erro: erroEmpresasEdicao,
  } = useEmpresasDoGrupo(itemEdicao ? formEdicao.clienteNovo?.id : null);
  const ufTravadaEdicao = empresasEdicao.find((e) => e.cnpj === formEdicao.cnpjNovo)?.uf || null;
  const itemLegadoSemCnpj = Boolean(itemEdicao?.id_grupo_economico && !itemEdicao?.cnpj);
  /** Linha global aberta no Editar: NCM, Item e escopo ficam travados (decisão 09-30). */
  const linhaGlobalEdicao = Boolean(itemEdicao) && ehLinhaGlobal(itemEdicao);
  /**
   * Alíquota/método travam quando o item vai FICAR global (sem Cliente) e falta a permissão de
   * edição global. "Vai ficar", não "é": um item de cliente que tem o Cliente limpo aqui vira
   * global ao salvar — mesma regra, senão a trava seria contornável por esse caminho.
   */
  const aliquotaMetodoBloqueadosEdicao =
    Boolean(itemEdicao) && !formEdicao.clienteNovo && !podeEditarGlobal;

  const montarLinhas = useCallback(
    (linhas) =>
      linhas.map((item) => ({
        cliente: celulaCliente(item.grupo_economico_nome),
        cnpj: celulaCnpj(item),
        estado: celulaEstado(item.estado),
        item: item.item,
        cest: formatarCest(item.cest),
        ncm: formatarNcm(item.ncm),
        descricao: celulaDescricao(item.descricao),
        tipo: celulaTipo(item.tipo),
        classificacao: celulaClassificacao(item.classificacao),
        valorMva: formatarMva(item.mva),
        aliquotaInterna: formatarAliquota(item.aliq_interna),
        metodoCalculo: celulaMetodo(item.metodo_calculo),
        // Sem `ncm-table-edit` a linha não leva botão — e hasActions={podeEditar} tira a coluna
        // Ações inteira (cabeçalho sem célula desalinharia a tabela).
        ...(podeEditar && {
          botoes: [
            { icon: <EditIcon />, title: "EDITAR", onClick: () => abrirEdicao(item) },
          ],
        }),
      })),
    // abrirEdicao fica de fora de propósito: só usa setters e gruposRef (ver comentário lá em
    // cima), então a versão do 1º render continua correta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar]
  );

  // As linhas formatadas derivam dos registros crus + permissão. minhas-permissoes é
  // assíncrono: se as chaves chegarem depois da 1ª carga, a coluna Ações aparece/some sem
  // recarregar a tela. É o único lugar que monta `data` a partir de `itens`.
  useEffect(() => {
    setData(montarLinhas(itens));
  }, [montarLinhas, itens]);

  /**
   * Parâmetros explícitos em vez de ler o state: a busca com debounce e os selects disparam
   * fora do ciclo de render e pegariam valores defasados.
   */
  const carregar = useCallback(
    async ({ pagina = 1, porPagina, filtrosAtuais } = {}) => {
      const tamanho = porPagina ?? perPage;
      const f = filtrosAtuais ?? filtros;

      setLoading(true);
      try {
        const resposta = await buscarTabelaNcm({
          page: pagina,
          per_page: tamanho,
          search: f.search,
          // Sentinela "Todas as Empresas (sem cliente)" é mutuamente exclusivo com um grupo
          // específico — nunca manda os dois juntos pra API.
          id_grupo_economico: f.cliente === SEM_CLIENTE ? undefined : f.cliente,
          somenteSemCliente: f.cliente === SEM_CLIENTE || undefined,
          cnpj: f.cnpj === SEM_CNPJ ? undefined : f.cnpj,
          somenteSemCnpj: f.cnpj === SEM_CNPJ || undefined,
          estado: f.estado === SEM_ESTADO ? undefined : f.estado,
          somenteSemEstado: f.estado === SEM_ESTADO || undefined,
          tipo: f.tipo,
          classificacao: f.classificacao,
        });

        setPage(pagina);
        setPerPage(tamanho);
        setTotal(resposta.pagination?.total ?? 0);
        const brutos = resposta.data || [];
        setItens(brutos);
        setData(montarLinhas(brutos));
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: error.response?.data?.message || "Falha ao carregar a Tabela NCM.",
        });
      } finally {
        setLoading(false);
      }
    },
    [filtros, perPage, montarLinhas]
  );

  useEffect(() => {
    const iniciar = async () => {
      try {
        setGrupos(await carregarComboItens("GRUPOECONOMICO", buscarComboPorCodigo));
      } catch (error) {
        setObjSnackBar({ type: "error", msg: "Falha ao carregar a lista de clientes." });
      }
      await carregar({ pagina: 1 });
    };
    iniciar();
    // Carga inicial apenas — as recargas seguintes são disparadas por filtro/paginação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Qualquer alteração de filtro volta para a primeira página (card seção 2.3). */
  const aplicarFiltro = (parcial) => {
    const novos = { ...filtros, ...parcial };
    setFiltros(novos);
    carregar({ pagina: 1, filtrosAtuais: novos });
  };

  const limparFiltros = () => {
    setBuscaInput("");
    setFiltros(FILTROS_VAZIOS);
    carregar({ pagina: 1, filtrosAtuais: FILTROS_VAZIOS });
  };

  // Mesmo idioma de src/pages/Usuarios/index.js (debouncedSearch): useMemo pra criar o
  // debounce uma única vez, ref pra aplicarFiltro nunca disparar com closure velha.
  const aplicarFiltroRef = useRef(aplicarFiltro);
  aplicarFiltroRef.current = aplicarFiltro;
  const buscaComDebounce = useMemo(
    () => debounce((valor) => aplicarFiltroRef.current({ search: valor }), 400),
    []
  );

  const opcoesComTodos = (rotuloTodos, lista) => [
    { value: "", label: rotuloTodos },
    ...lista.map((v) => ({ value: v, label: v })),
  ];
  const clienteOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      { value: SEM_CLIENTE, label: "Todas as Empresas (sem cliente)" },
      ...grupos.map((g) => ({ value: String(g.id), label: g.detalhes })),
    ],
    [grupos]
  );
  const estadoOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      { value: SEM_ESTADO, label: "Todos os Estados (sem UF)" },
      ...UFS.map((uf) => ({ value: uf, label: uf })),
    ],
    []
  );
  // Opções do filtro de CNPJ = CNPJs que JÁ TÊM item na Tabela NCM (não todas as empresas do
  // grupo — essa é a fonte do cadastro, nos modais). Dependem só do filtro de Cliente: vazio →
  // cadastrados de todos os grupos; cliente específico → só os cadastrados dele; "Todas as
  // Empresas" → CNPJ não se aplica. Só o CNPJ formatado na opção, sem razão social (pedido do
  // usuário). "Sem CNPJ (pendente)" continua pros legados.
  const clienteFiltroEspecifico =
    filtros.cliente && filtros.cliente !== SEM_CLIENTE ? filtros.cliente : null;
  const [versaoCnpjs, setVersaoCnpjs] = useState(0);
  const { cnpjs: cnpjsFiltro } = useCnpjsCadastrados(
    clienteFiltroEspecifico,
    filtros.cliente !== SEM_CLIENTE,
    versaoCnpjs
  );
  const cnpjOpcoes = useMemo(() => {
    if (filtros.cliente === SEM_CLIENTE) return [{ value: "", label: "Todos" }];
    return [
      { value: "", label: "Todos" },
      { value: SEM_CNPJ, label: "Sem CNPJ (pendente)" },
      ...cnpjsFiltro.map((c) => ({ value: c.cnpj, label: formatarCnpj(c.cnpj) })),
    ];
  }, [filtros.cliente, cnpjsFiltro]);
  const tipoOpcoes = useMemo(() => opcoesComTodos("Todos", TIPOS_ITEM), []);
  const classificacaoOpcoes = useMemo(() => opcoesComTodos("Todas", CLASSIFICACOES), []);

  const salvarEdicao = async (e) => {
    e.preventDefault();
    // Card seção 2.4: só NCM/SH e Descrição são obrigatórios na edição — CEST, Tipo,
    // Classificação e Valor MVA podem ficar vazios. Mensagem única, igual ao protótipo.
    if (!formEdicao.ncm.trim() || !formEdicao.descricao.trim()) {
      return setObjSnackBar({ type: "error", msg: "Informe NCM/SH e Descrição." });
    }
    if (formEdicao.clienteNovo && !formEdicao.cnpjNovo) {
      return setObjSnackBar({ type: "error", msg: "Selecione o CNPJ do cliente." });
    }

    setSalvandoEdicao(true);
    try {
      const resposta = await atualizarItemTabelaNcm(itemEdicao.id, {
        cest: formEdicao.cest.trim() || null,
        descricao: formEdicao.descricao.trim(),
        tipo: formEdicao.tipo,
        classificacao: formEdicao.classificacao,
        mva: formEdicao.mva.trim() || null,
        // NCM + Cliente/CNPJ/Estado: linha de cliente manda o valor atual dos campos (editáveis
        // em qualquer direção); linha global não manda nada disso (imutáveis, decisão 09-30).
        ...payloadNcmEscopoEdicao(itemEdicao, formEdicao),
        // Só o que mudou. Travados (global sem permissão) não mudam, então não vão no PUT —
        // e a API não precisa recusar com SEM_PERMISSAO_EDITAR_GLOBAL uma edição só de descrição.
        ...(!aliquotaMetodoBloqueadosEdicao && payloadAliquotaMetodoAlterados(itemEdicao, formEdicao)),
      });
      setObjSnackBar({ type: "success", msg: resposta.message || "Item atualizado." });
      setItemEdicao(null);
      // Troca a linha editada pelo registro que o PUT devolve, em vez de refazer a consulta:
      // com um filtro que o item deixou de atender (ex.: "Sem CNPJ (pendente)" depois de
      // escolher o CNPJ), a consulta o tiraria da lista e a edição pareceria não ter sido
      // salva. A linha fica visível até a próxima troca de filtro/página. Item não muda na
      // edição, então a posição na lista (ORDER BY item) continua a mesma.
      const salvo = resposta?.data;
      if (salvo) {
        const novos = itens.map((i) => (String(i.id) === String(salvo.id) ? salvo : i));
        setItens(novos);
        setData(montarLinhas(novos));
      } else {
        await carregar({ pagina: page });
      }
      // O item pode ter levado um CNPJ que ainda não tinha nenhum item — recarrega o filtro.
      setVersaoCnpjs((v) => v + 1);
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.message || "Falha ao atualizar o item.",
      });
    } finally {
      setSalvandoEdicao(false);
    }
  };

  /**
   * Mesmo tratamento visual do painel de filtros das telas de Apuração ICMS
   * (ApuracaoCardShell: backgroundColor #f5f7fa) — o TableCard sozinho deixa o fundo branco
   * (.filtrosContainer do CSS module), e não dá pra mudar esse módulo compartilhado sem
   * afetar toda tela que usa TableCard. Aplicado só aqui, por cima.
   */
  const customFilters = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "minmax(240px, 1.6fr) repeat(5, minmax(140px, 1fr)) auto",
        },
        alignItems: "center",
        gap: 2,
        flex: 1,
        width: "100%",
        bgcolor: "#f5f7fa",
        border: "1px solid #e8eef3",
        borderRadius: "10px",
        p: 2,
      }}
    >
      <TextField
        size="small"
        placeholder="Item, CEST, NCM/SH ou Descrição"
        value={buscaInput}
        onChange={(e) => {
          setBuscaInput(e.target.value);
          buscaComDebounce(e.target.value);
        }}
        sx={{ ...FILTER_FIELD_SX, width: "100%" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#3c4043" }} fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {/* width={140}: sem ele o SelectFiltro tem largura fixa de 220px, maior que a coluna do
          grid (~186px com 5 filtros) — cada campo transbordava por cima do gap e os filtros
          pareciam colados. Com width, o campo ocupa exatamente a célula (mínimo 140px). */}
      <SelectFiltro width={140} label="Cliente" value={filtros.cliente} onChange={(v) => aplicarFiltro({ cliente: v, cnpj: "" })} options={clienteOpcoes} />
      <SelectFiltro width={140} label="CNPJ" value={filtros.cnpj} onChange={(v) => aplicarFiltro({ cnpj: v })} options={cnpjOpcoes} />
      <SelectFiltro width={140} label="Estado" value={filtros.estado} onChange={(v) => aplicarFiltro({ estado: v })} options={estadoOpcoes} />
      <SelectFiltro width={140} label="Tipo" value={filtros.tipo} onChange={(v) => aplicarFiltro({ tipo: v })} options={tipoOpcoes} />
      <SelectFiltro width={140} label="Classificação" value={filtros.classificacao} onChange={(v) => aplicarFiltro({ classificacao: v })} options={classificacaoOpcoes} />
      <Button
        variant="outlined"
        size="small"
        onClick={limparFiltros}
        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
      >
        Limpar
      </Button>
    </Box>
  );

  /**
   * hoverBtnHeader (AtualizarSelic) some pra uma linha própria quando customFiltersFullWidth
   * força o painel de filtros a 100% — flex-wrap quebra a linha antes dele. actionHeader é a
   * prop nova (aditiva, só usada aqui) que fica garantida na mesma linha do título, igual ao
   * "+ Novo Registro" do protótipo. Botão retangular (Button variant="contained"), mesmo
   * padrão do "Novo Usuário" em src/pages/Usuarios/index.js — não o pill do BtnGenericHeader.
   */
  const actionHeader = (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={() => setOpenNovo(true)}
      sx={{
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: 600,
        bgcolor: "#18316e",
        minHeight: 40,
        whiteSpace: "nowrap",
        px: 2,
        "&:hover": { bgcolor: "#152858" },
      }}
    >
      Novo Registro
    </Button>
  );

  return (
    <>
      <TableCard
        loading={loading}
        iconHeader={<FactCheckIcon fontSize="1rem" />}
        titleHeader="Tabela NCM"
        data={data}
        setData={setData}
        columns={COLUNAS}
        // Padding menor + título que quebra linha (styles.module.css), só nesta tabela.
        tableClassName={styles.tabelaNcm}
        textHeaderCard="item"
        customFilters={customFilters}
        customFiltersFullWidth
        hideViewToggle
        // Sem `ncm-table-create` não há "+ Novo Registro" (TableCard só renderiza se vier algo).
        actionHeader={podeCriar ? actionHeader : undefined}
        // Sem `ncm-table-edit` as linhas não têm botão (montarLinhas) — tira a coluna Ações junto.
        hasActions={podeEditar}
        // Ações presa à direita (mesmo opt-in da tela de Usuários): em tela mais estreita a
        // tabela ainda rola, mas o botão Editar continua sempre visível.
        stickyActions
        serverPagination={{
          total,
          page,
          rowsPerPage: perPage,
          onPageChange: (p) => carregar({ pagina: p }),
          onRowsPerPageChange: (n) => carregar({ pagina: 1, porPagina: n }),
        }}
      />

      <ModalNovoRegistro
        open={openNovo}
        onClose={() => setOpenNovo(false)}
        grupos={grupos}
        podeEditarGlobal={podeEditarGlobal}
        onConcluido={() => {
          carregar({ pagina: 1 });
          setVersaoCnpjs((v) => v + 1);
        }}
        setObjSnackBar={setObjSnackBar}
      />

      {/* Layout espelhando o .modal-grid do protótipo (2 colunas, .span-2 = linha inteira) e
          o .modal-dialog base: max-width 520px, border-radius 10px. */}
      <Dialog
        open={Boolean(itemEdicao)}
        onClose={() => setItemEdicao(null)}
        fullWidth
        PaperProps={{ sx: { borderRadius: "10px", maxWidth: 520 } }}
      >
        {salvandoEdicao ? (
          <Loading loading={salvandoEdicao} />
        ) : (
          <form onSubmit={salvarEdicao}>
            <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: "#0d2847" }}>
              Editar item
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3, py: 2.75 }}>
              <Box sx={GRID_MODAL}>
                {/* Item é a única identidade travada (card seção 4.5). Cliente e Estado são
                    editáveis nas duas direções (decisão de produto 2026-09-21, revisada no
                    mesmo dia: a trava "só anda de Todas as Empresas pra específico" foi
                    removida por impedir corrigir uma reivindicação errada) — mesmo
                    Autocomplete com seta de expandir/selecionar do modal Novo Registro,
                    limpável de volta pra "Todas as Empresas"/"Todos os Estados". A única
                    proteção que resta é o índice único no banco: gravar uma combinação que já
                    existe em outro item vira erro ao salvar, não uma trava no campo.
                    Exceção (decisão 09-30): na linha global, NCM, Item, Cliente, CNPJ e Estado
                    ficam travados — a linha vale para todos os clientes. */}
                {linhaGlobalEdicao && (
                  <Typography
                    sx={{
                      ...SPAN_2_MODAL,
                      fontSize: 13,
                      color: "#0d2847",
                      bgcolor: "#eef4fb",
                      border: "1px solid #cfdcec",
                      borderRadius: "6px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    {MOTIVO_ESCOPO_GLOBAL_IMUTAVEL}
                  </Typography>
                )}
                <Box sx={SPAN_2_MODAL}>
                  <Autocomplete
                    size="small"
                    options={grupos}
                    value={formEdicao.clienteNovo}
                    disabled={linhaGlobalEdicao}
                    // Trocar o Cliente invalida CNPJ e Estado — pertenciam à empresa anterior.
                    onChange={(_, valor) =>
                      setFormEdicao({ ...formEdicao, clienteNovo: valor, cnpjNovo: null, estadoNovo: null })
                    }
                    getOptionLabel={(opcao) => opcao.detalhes || ""}
                    isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Cliente" placeholder="Todas as Empresas" />
                    )}
                  />
                  {!formEdicao.clienteNovo && !linhaGlobalEdicao && (
                    <Typography component="span" sx={HINT_MODAL}>
                      Deixe em branco para aplicar a "Todas as Empresas".
                    </Typography>
                  )}
                </Box>
                {formEdicao.clienteNovo && (
                  <Box>
                    <CampoCnpj
                      empresas={empresasEdicao}
                      carregando={carregandoEmpresasEdicao}
                      erroCarga={erroEmpresasEdicao}
                      value={formEdicao.cnpjNovo}
                      onChange={(empresa) =>
                        setFormEdicao({
                          ...formEdicao,
                          cnpjNovo: empresa?.cnpj || null,
                          estadoNovo: empresa?.uf || null,
                        })
                      }
                      hint={
                        itemLegadoSemCnpj && !formEdicao.cnpjNovo
                          ? "Item cadastrado antes do CNPJ ser obrigatório — escolha o CNPJ para salvar."
                          : "Obrigatório quando há Cliente."
                      }
                    />
                  </Box>
                )}
                {/* Sem Cliente, Estado ocupa a linha inteira (não há CNPJ ao lado). */}
                <Box sx={formEdicao.clienteNovo ? undefined : SPAN_2_MODAL}>
                  <Autocomplete
                    size="small"
                    options={UFS}
                    value={formEdicao.estadoNovo}
                    onChange={(_, valor) => setFormEdicao({ ...formEdicao, estadoNovo: valor })}
                    disabled={Boolean(ufTravadaEdicao) || linhaGlobalEdicao}
                    renderInput={(params) => (
                      <TextField {...params} label="Estado" placeholder="Todos os Estados" />
                    )}
                  />
                  {/* Na linha global o motivo da trava já está no aviso do topo. */}
                  {!linhaGlobalEdicao && (
                    <Typography component="span" sx={HINT_MODAL}>
                      {ufTravadaEdicao
                        ? "UF cadastrada para o CNPJ selecionado."
                        : 'Deixe em branco para aplicar a "Todos os Estados".'}
                    </Typography>
                  )}
                </Box>
                <Box sx={SPAN_2_MODAL}>
                  <TextField
                    label="Item"
                    value={itemEdicao?.item || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Typography component="span" sx={HINT_MODAL}>
                    O código do item não pode ser alterado aqui.
                  </Typography>
                </Box>
                <TextField
                  label="CEST"
                  placeholder="Ex.: 21.077.00"
                  value={formEdicao.cest}
                  onChange={(e) => setFormEdicao({ ...formEdicao, cest: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="NCM/SH"
                  placeholder="Ex.: 3002.42.00"
                  value={formEdicao.ncm}
                  onChange={(e) => setFormEdicao({ ...formEdicao, ncm: e.target.value })}
                  disabled={linhaGlobalEdicao}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Descrição"
                  placeholder="Descrição do item"
                  value={formEdicao.descricao}
                  onChange={(e) => setFormEdicao({ ...formEdicao, descricao: e.target.value })}
                  fullWidth
                  size="small"
                  sx={SPAN_2_MODAL}
                />
                <Autocomplete
                  size="small"
                  options={TIPOS_ITEM}
                  value={formEdicao.tipo}
                  onChange={(_, valor) => setFormEdicao({ ...formEdicao, tipo: valor })}
                  renderInput={(params) => <TextField {...params} label="Tipo" />}
                  sx={SPAN_2_MODAL}
                />
                <Autocomplete
                  size="small"
                  options={CLASSIFICACOES}
                  value={formEdicao.classificacao}
                  onChange={(_, valor) => setFormEdicao({ ...formEdicao, classificacao: valor })}
                  renderInput={(params) => <TextField {...params} label="Classificação" />}
                />
                <TextField
                  label="Valor MVA (%)"
                  type="number"
                  inputProps={{ step: 0.01, min: 0 }}
                  placeholder="Ex.: 39,50"
                  value={formEdicao.mva}
                  onChange={(e) => setFormEdicao({ ...formEdicao, mva: e.target.value })}
                  fullWidth
                  size="small"
                />
                <CamposAliquotaMetodo
                  aliquota={formEdicao.aliquota}
                  metodo={formEdicao.metodo}
                  onChange={(parcial) => setFormEdicao({ ...formEdicao, ...parcial })}
                  bloqueado={aliquotaMetodoBloqueadosEdicao}
                  motivoBloqueio={MOTIVO_SEM_PERMISSAO_GLOBAL}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pt: 2, pb: 2.75 }}>
              <WhiteButton clickFunction={() => setItemEdicao(null)} textValue="Cancelar" />
              <BlueButton textValue="Salvar" type="submit" />
            </DialogActions>
          </form>
        )}
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default TabelaNcm;
